import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PlayerNoteAdder from "../components/PlayerNoteAdder";
import { toast } from "sonner";
import { CirclePlus, X, Trash, TrafficCone, MapPinned, LoaderCircle, ClipboardList, Check, UserRoundCheck } from "lucide-react";

const inputCls = "w-full px-3 py-2.5 rounded-xl bg-white border border-[#d1fae5] focus:border-[#2a3b19] outline-none text-sm";

const emptySession = {
  date: "", time: "", venue: "", objective: "", drills: [],
  attendance: [], evaluations: [], general_notes: "",
};

export default function Training() {
  const [sessions, setSessions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [editing, setEditing] = useState(null);       // details editor
  const [creating, setCreating] = useState(false);     // minimal create modal
  const [createForm, setCreateForm] = useState({ date: "", time: "", venue: "", objective: "" });
  const [saving, setSaving] = useState(false);
  const [drillName, setDrillName] = useState("");

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/trainings"), api.get("/players")]);
    setSessions(s.data);
    setPlayers(p.data);
  };
  useEffect(() => { load(); }, []);

  // open the details editor for an existing session (all players default present)
  const openDetails = (s) => {
    const base = { ...emptySession, ...s };
    base.attendance = players.map((pl) => {
      const ex = (s.attendance || []).find((a) => a.player_id === pl.id);
      return ex ? { player_id: pl.id, status: ex.status === "absent" ? "absent" : "present" } : { player_id: pl.id, status: "present" };
    });
    base.evaluations = (s.evaluations || []).map((e) => ({ ...e, rating: e.rating == null ? "" : e.rating }));
    setEditing(base);
  };

  const startCreate = () => { setCreateForm({ date: "", time: "", venue: "", objective: "" }); setCreating(true); };

  const createSession = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/trainings", { ...emptySession, ...createForm });
      toast.success("تم إنشاء الحصة، أضف التفاصيل الآن");
      setCreating(false);
      await load();
      openDetails(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const setAtt = (pid, status) =>
    setEditing((e) => ({ ...e, attendance: e.attendance.map((a) => a.player_id === pid ? { ...a, status } : a) }));

  const upsertEval = (item) =>
    setEditing((e) => {
      const exists = e.evaluations.find((x) => x.player_id === item.player_id);
      const evals = exists ? e.evaluations.map((x) => x.player_id === item.player_id ? { ...x, ...item } : x) : [...e.evaluations, item];
      return { ...e, evaluations: evals };
    });
  const removeEval = (pid) => setEditing((e) => ({ ...e, evaluations: e.evaluations.filter((x) => x.player_id !== pid) }));

  const addDrill = () => {
    if (!drillName.trim()) return;
    setEditing((e) => ({ ...e, drills: [...e.drills, { name: drillName }] }));
    setDrillName("");
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...editing,
      evaluations: editing.evaluations
        .filter((e) => e.rating !== "" || e.note)
        .map((e) => ({ ...e, rating: e.rating === "" ? null : parseInt(e.rating) })),
    };
    try {
      await api.put(`/trainings/${editing.id}`, payload);
      toast.success("تم حفظ الحصة");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف الحصة؟")) return;
    await api.delete(`/trainings/${id}`);
    load();
  };

  const nameOf = (id) => players.find((p) => p.id === id)?.name || "لاعب";
  const presentCount = editing ? editing.attendance.filter((a) => a.status === "present").length : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium text-[#2a3b19] uppercase tracking-wider">الجدول</p>
          <h1 className="text-3xl font-black">الحصص التدريبية</h1>
        </div>
        <button data-testid="add-training-btn" onClick={startCreate} className="px-5 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center gap-2">
          <CirclePlus size={18} /> حصة جديدة
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sessions.map((s) => (
          <div key={s.id} data-testid={`training-card-${s.id}`} className="glass border border-[#d1fae5] rounded-2xl p-5 hover:border-[#2a3b19]/40 transition-colors cursor-pointer" onClick={() => openDetails(s)}>
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-[#2a3b19]/10 flex items-center justify-center"><TrafficCone className="text-[#2a3b19]" size={22} /></div>
              <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="text-red-500 hover:text-red-400" data-testid={`delete-training-${s.id}`}><Trash size={16} /></button>
            </div>
            <h3 className="font-bold mt-3">{s.objective || "حصة تدريبية"}</h3>
            <div className="text-xs text-slate-600 mt-2 space-y-1">
              <div>{s.date} {s.time && `· ${s.time}`}</div>
              {s.venue && <div className="flex items-center gap-1"><MapPinned size={12} /> {s.venue}</div>}
              <div>{(s.drills || []).length} تمرين · {(s.attendance || []).filter(a => a.status === "present").length} حاضر · {(s.evaluations || []).length} ملاحظة</div>
            </div>
          </div>
        ))}
        {sessions.length === 0 && <p className="text-slate-500 col-span-full text-center py-12">لا توجد حصص بعد.</p>}
      </div>

      {/* STEP 1: minimal create modal */}
      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCreating(false)}>
          <div onClick={(e) => e.stopPropagation()} className="glass border border-[#d1fae5] rounded-2xl p-6 w-full max-w-md" data-testid="training-create-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">إنشاء حصة تدريبية</h2>
              <button onClick={() => setCreating(false)}><X size={22} className="text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input type="date" data-testid="tr-date" value={createForm.date} onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })} className={inputCls} />
                <input type="time" value={createForm.time} onChange={(e) => setCreateForm({ ...createForm, time: e.target.value })} className={inputCls} />
              </div>
              <input placeholder="المكان" value={createForm.venue} onChange={(e) => setCreateForm({ ...createForm, venue: e.target.value })} className={inputCls} />
              <input placeholder="هدف الحصة" data-testid="tr-objective" value={createForm.objective} onChange={(e) => setCreateForm({ ...createForm, objective: e.target.value })} className={inputCls} />
            </div>
            <button onClick={createSession} disabled={saving} data-testid="create-training-btn" className="w-full mt-5 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center justify-center gap-2">
              {saving && <LoaderCircle className="animate-spin" size={18} />} إنشاء ومتابعة التفاصيل
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: details editor */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="glass border border-[#d1fae5] rounded-2xl p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="training-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">{editing.objective || "تفاصيل الحصة"}</h2>
              <button onClick={() => setEditing(null)}><X size={22} className="text-slate-500" /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className={inputCls} />
              <input type="time" value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} className={inputCls} />
              <input placeholder="المكان" value={editing.venue} onChange={(e) => setEditing({ ...editing, venue: e.target.value })} className={inputCls} />
              <input placeholder="هدف الحصة" value={editing.objective} onChange={(e) => setEditing({ ...editing, objective: e.target.value })} className={inputCls} />
            </div>

            <h3 className="font-bold mb-2 flex items-center gap-2"><ClipboardList size={16} className="text-[#2a3b19]" /> خطة التمارين</h3>
            <div className="flex gap-2 mb-2">
              <input value={drillName} onChange={(e) => setDrillName(e.target.value)} placeholder="اسم التمرين" className={inputCls} data-testid="drill-input" onKeyDown={(e) => e.key === "Enter" && addDrill()} />
              <button onClick={addDrill} className="px-4 rounded-xl bg-[#ecfdf5] hover:bg-[#d1fae5]" data-testid="add-drill-btn"><CirclePlus size={18} /></button>
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              {editing.drills.map((d, i) => (
                <span key={i} className="text-xs bg-[#2a3b19]/10 text-[#2a3b19] px-3 py-1.5 rounded-lg flex items-center gap-2">
                  {d.name}
                  <button onClick={() => setEditing({ ...editing, drills: editing.drills.filter((_, j) => j !== i) })}><X size={12} /></button>
                </span>
              ))}
            </div>

            <h3 className="font-bold mb-2 flex items-center gap-2"><UserRoundCheck size={16} className="text-[#2a3b19]" /> تسجيل الحضور <span className="text-xs font-normal text-slate-500">({presentCount} حاضر من {editing.attendance.length})</span></h3>
            <div className="space-y-1.5 mb-6">
              {editing.attendance.map((a) => (
                <div key={a.player_id} className="flex items-center justify-between bg-white border border-[#d1fae5] rounded-xl px-3 py-2" data-testid={`att-row-${a.player_id}`}>
                  <span className="font-medium text-sm">{nameOf(a.player_id)}</span>
                  <div className="flex gap-2">
                    <button onClick={() => setAtt(a.player_id, "present")} data-testid={`att-present-${a.player_id}`} title="حاضر"
                      className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold transition-colors ${a.status === "present" ? "bg-[#2a3b19] text-white" : "bg-[#ecfdf5] text-[#2a3b19] hover:bg-[#d1fae5]"}`}>
                      <Check size={18} />
                    </button>
                    <button onClick={() => setAtt(a.player_id, "absent")} data-testid={`att-absent-${a.player_id}`} title="غائب"
                      className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold transition-colors ${a.status === "absent" ? "bg-red-500 text-white" : "bg-red-50 text-red-500 hover:bg-red-100"}`}>
                      <X size={18} />
                    </button>
                  </div>
                </div>
              ))}
              {editing.attendance.length === 0 && <p className="text-slate-500 text-sm">أضف لاعبين أولاً من صفحة اللاعبين.</p>}
            </div>

            <h3 className="font-bold mb-2">ملاحظات وتقييمات اللاعبين</h3>
            <PlayerNoteAdder players={players} evaluations={editing.evaluations} onSave={upsertEval} onRemove={removeEval} testidPrefix="tr-eval" />

            <h3 className="font-bold mt-6 mb-2">ملاحظات عامة على الحصة</h3>
            <textarea placeholder="ملاحظات عامة تخص الحصة كاملة" value={editing.general_notes} onChange={(e) => setEditing({ ...editing, general_notes: e.target.value })} className={inputCls} rows={2} data-testid="tr-general-notes" />

            <button onClick={save} disabled={saving} data-testid="save-training-btn" className="w-full mt-5 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center justify-center gap-2">
              {saving && <LoaderCircle className="animate-spin" size={18} />} حفظ التفاصيل
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
